import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, message } from "antd";
import { Link, useLocation, useHistory } from "react-router-dom";
import qs from "qs";
import { Header, Loading, Icon, Button, Locate } from "@/components";
import InfiniteScroll from "react-infinite-scroller";
import { getQuery } from "@/service/http";
import orderAPI from "@/service/order";
import EmptyBooking from "@/images/no-order.svg";
import DealCard from "@/components/buy-deal/deal-card";
import { ConfirmModal } from "@/components/buy-deal/common";
import OrderDetail from "@/components/buy-deal/deal-order";
import orderQtyImg from "@/components/buy-deal/images/order_qty.svg";
import "./index.less";

let loading = false;

/**
 * 我的订单
 *
 * 切换tab查询不同状态的订单列表
 * 可查看订单详情，继续支付，退款，取消操作
 *
 */
const MyOrders = ({ payment }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [tab, setTab] = useState("all");
  const [showDetail, setShowDetail] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [list, setList] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const ref = useRef({});
  const baseUrl = `${document.referrer}${getQuery().lang}/members/bookings?`;
  const statusList = [
    {
      label: t("walkin.All"),
      value: "all",
    },
    {
      label: t("walkin.Available"),
      value: "available",
    },
    {
      label: t("walkin.Unpaid"),
      value: "unpaid",
    },
    {
      label: t("walkin.Refunds"),
      value: "refunds",
    },
  ];

  function loadMore(page) {
    getOrders(page, tab);
  }

  /**获取预定列表 */
  async function getOrders(page, type = "all") {
    try {
      if (loading) return;
      loading = true;
      const { data, page_info } = await orderAPI.getOrders({
        type,
        page,
      });
      setList((list) => list.concat(data));
      setHasMore(page_info.pages > page);
      loading = false;
    } catch (error) {
      console.error(error);
      loading = false;
    }
  }

  function openDetails(uuid, deal) {
    ref.current.uuid = uuid;
    ref.current.payment = qs.stringify(
      {
        order_id: uuid,
      },
      { delimiter: "-" }
    );
    ref.current.deal = deal;
    setShowDetail(true);
  }

  function onAction(event, type, order) {
    event.stopPropagation();
    switch (type) {
      case "cancel":
        onCancel(order.uuid, order.entry);
        break;
      case "refund":
        onRefund(order.uuid, order.entry);
        break;
      default:
        openDetails(order.uuid, order.entry);
        break;
    }
  }

  /**发起退款 */
  function onRefund(uuid, deal) {
    setShowRefundModal(true);
    ref.current.refundCallback = () => {
      if (loading) return;
      loading = true;
      orderAPI
        .cancelOrder(uuid)
        .then(() => {
          setShowRefundModal(false);
          openDetails(uuid, deal);
          loading = false;
        })
        .catch((err) => {
          loading = false;
          message.error(err?.data?.full_messages?.[0] || err.message);
        });
    };
  }

  /**取消订单 */
  function onCancel(uuid, deal) {
    setShowCancelModal(true);
    ref.current.cancelCallback = () => {
      if (loading) return;
      loading = true;
      orderAPI
        .cancelOrder(uuid)
        .then(() => {
          setShowCancelModal(false);
          openDetails(uuid, deal);
          loading = false;
        })
        .catch((err) => {
          loading = false;
          message.error(err?.data?.full_messages?.[0] || err.message);
        });
    };
  }

  /**关闭详情窗口，需要重新获取订单，并重新获取订单列表 */
  function closeDetail() {
    setShowDetail(false);
    orderAPI.getOrderById(ref.current.uuid).then((order) => {
      setList((list) =>
        list.map((item) => {
          if (item.id === order.id) {
            return { ...item, ...order };
          }
          return item;
        })
      );
    });
  }

  // 点击deal卡片
  function onDealClick(deal) {
    const {
      lang = "en",
      region = "shanghai",
      private_token,
      callback,
      login_url,
      source = "",
    } = getQuery();
    history.push(
      `/deals/${deal.id}?lang=${lang}&source=${source}&region=${region}&private_token=${private_token}&callback=${callback}&login_url=${login_url}`
    );
    // 窗口url要定位为website_detail_url
    if (
      process.env.REACT_APP_MODE !== "DEV" &&
      window.parent &&
      deal.website_detail_url
    ) {
      window.location.href = `/#/deals/${deal.id}?lang=${lang}&source=${source}&region=${region}&private_token=${private_token}&callback=${callback}&login_url=${login_url}`;
      window.top.postMessage(
        {
          type: "other",
          params: {
            url: deal.website_detail_url,
            region,
            lang,
          },
        },
        "*"
      );
    }
  }

  useEffect(() => {
    if (payment) {
      const payCallbackParams = qs.parse(payment || "", { delimiter: "-" });
      openDetails(payCallbackParams.order_id);
    }
  }, []);

  useEffect(() => {
    setHasMore(false);
    setList([]);
    getOrders(1, tab);
  }, [tab]);

  return (
    <React.Fragment>
      <Locate />
      <div className="page-orders">
        <div className="order-list">
          <div className="order-nav">
            {statusList.map((item, index) => {
              return (
                <span
                  key={index}
                  data-selected={tab === item.value}
                  onClick={() => {
                    setTab(item.value);
                  }}
                >
                  {item.label}
                </span>
              );
            })}
          </div>
          <div className="order-scroll" key={tab}>
            {!list.length ? (
              <div className="order-empty">
                <img src={EmptyBooking} alt="" />
                <div className="txt">{t("walkin.no_orders")}</div>
              </div>
            ) : (
              <InfiniteScroll
                initialLoad={false}
                pageStart={1}
                loadMore={loadMore}
                hasMore={hasMore}
                useWindow={false}
              >
                {list.map((item, index) => {
                  return (
                    <OrderItem
                      onAction={(evt, type) => onAction(evt, type, item)}
                      order={item}
                      openDetails={openDetails}
                      key={item.id + "_" + index}
                    />
                  );
                })}
                <div className="load-more">
                  {hasMore && <Loading red></Loading>}
                </div>
              </InfiniteScroll>
            )}
          </div>
        </div>
      </div>

      <Drawer
        visible={showDetail}
        onClose={closeDetail}
        placement="right"
        className="buy-deal-drawer booking-drawer profile-item-inner-drawer"
      >
        {showDetail && (
          <div className="buy-deal">
            <OrderDetail
              baseUrl={baseUrl}
              orderId={ref.current.uuid}
              showBack={true}
              hasAnimate={false}
              onDealClick={onDealClick}
              onBack={closeDetail}
            />
          </div>
        )}
      </Drawer>
      {showCancelModal && (
        <ConfirmModal
          onOk={ref.current.cancelCallback}
          onCancel={() => setShowCancelModal(false)}
        >
          {t("walkin.cancel tip")}
        </ConfirmModal>
      )}

      {showRefundModal && (
        <ConfirmModal
          onOk={ref.current.refundCallback}
          onCancel={() => setShowRefundModal(false)}
        >
          {t("walkin.refund tip")}
        </ConfirmModal>
      )}
    </React.Fragment>
  );
};

const OrderItem = ({ onAction, order, openDetails }) => {
  const { quantity, page_status, page_status_humanize, entity } = order;
  return (
    <div
      className="order-content"
      onClick={() => openDetails(order.uuid, entity)}
    >
      <div className="order-item hairline-surround" status={page_status}>
        {!!entity && <DealCard deal={entity}></DealCard>}
        <footer className="order-footer">
          <div className="status-button" status={page_status}>
            <div className="qty">
              <img
                src={orderQtyImg}
                alt=""
              />
              <span>{quantity}</span>
            </div>
            {/* 已兑换和已关闭是灰色，其他都是红色 */}
            <div className="status-text" data-status={page_status}>
              {page_status_humanize}
            </div>
          </div>
          <ActionButton onAction={onAction} {...order} />
        </footer>
      </div>
    </div>
  );
};

/**操作按钮 */
const ActionButton = ({ onAction, uuid, operations, entity }) => {
  let { t } = useTranslation();
  const { can_cancel, can_redeem, can_refund, can_pay } = operations || {};
  return (
    <div className="action-button-list">
      {can_cancel && (
        <Button
          onClick={(evt) => {
            onAction(evt, "cancel");
          }}
        >
          {t("CANCEL")}
        </Button>
      )}
      {can_pay && (
        <Button
          type="primary"
          onClick={(evt) => {
            onAction(evt, "pay");
          }}
        >
          {t("PAYMENT.Pay now")}
        </Button>
      )}
      {can_refund && (
        <Button
          onClick={(evt) => {
            onAction(evt, "refund");
          }}
        >
          {t("walkin.Request Refund")}
        </Button>
      )}
      {can_redeem && (
        <Button
          type="primary"
          onClick={(evt) => {
            onAction(evt, "redeem");
          }}
        >
          {t("walkin.Use Now")}
        </Button>
      )}
    </div>
  );
};

export default MyOrders;
